import { create } from 'zustand';

export interface AnalyticsRealtimeMetrics {
  currentWip: number;
  averageLeadTimeMinutes: number;
  activeCount: number;
  slaRiskPercentage: number;
  throughput: number;
}

export interface AnalyticsHistoricalMetrics {
  leadTimeTrend: any[];
  throughputTrend: any[];
  stagePerformance: any[];
  financialImpact: any[];
  pauseReasonPareto?: { reason: string; count: number; percentage: number; cumulativePercentage: number }[];
}

interface AnalyticsState {
  realtime: AnalyticsRealtimeMetrics | null;
  historical: AnalyticsHistoricalMetrics | null;
  
  // Ações de preenchimento do repositório
  setRealtimeData: (data: AnalyticsRealtimeMetrics) => void;
  setHistoricalData: (data: AnalyticsHistoricalMetrics) => void;
  
  // Cálculo centralizado
  calculateMetrics: (
    executions: any[], 
    historicalExecutions: any[],
    organizationHourlyRate?: number
  ) => void;
}

export const useAnalyticsStore = create<AnalyticsState>((set) => ({
  realtime: null,
  historical: null,

  setRealtimeData: (data) => set({ realtime: data }),
  setHistoricalData: (data) => set({ historical: data }),

  calculateMetrics: (executions, historicalExecutions, organizationHourlyRate = 150) => {
    // --- 1. REALTIME (Status Atual) ---
    const active = executions.filter(e => e.status !== 'completed' && e.status !== 'canceled');
    const closed = executions.filter(e => e.status === 'completed' || e.status === 'resolved' || e.status === 'closed');
    
    const activeCount = active.length;
    const currentWip = active.reduce((acc, curr) => acc + (curr.activeTasksCount || 1), 0);
    
    let totalLeadTime = 0;
    let slaAtRiskCount = 0;
    
    closed.forEach(e => {
      totalLeadTime += (e.elapsedMinutes ?? e.actual_lead_time_minutes ?? 0);
    });
    active.forEach(e => {
      // Risk arbitrary simulation
      if (e.isOverdue || (e.sla_rate && e.sla_rate < 50)) slaAtRiskCount++;
    });

    const averageLeadTimeMinutes = closed.length > 0 ? totalLeadTime / closed.length : 0;
    const slaRiskPercentage = activeCount > 0 ? (slaAtRiskCount / activeCount) * 100 : 0;
    
    // Throughput realtime = items finished today or similar, relying on closed count relative over period. 
    // Here we just map general throughput.
    const throughput = closed.length;

    // --- 2. HISTÓRICO & TENDÊNCIA & IMPACTO FINANCEIRO ---
    // Financial Impact Logic: Se resolvido, (Target - Actual) * Hora
    let cumulativeSavings = 0;
    const impactSeries = historicalExecutions
      .filter((item: any) => item.status === 'closed' || item.status === 'resolved' || item.status === 'completed')
      .map((item: any) => {
        let savings = 0;
        const actual = item.elapsedMinutes ?? item.actual_lead_time_minutes ?? 0;
        const target = item.target_minutes ?? item.metadata?.expectedDurationMinutes ?? 0;
        
        if (target > 0 && actual < target) {
          const diffHours = (target - actual) / 60;
          savings = diffHours * organizationHourlyRate;
        }
        
        cumulativeSavings += savings;

        return {
          label: item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '...',
          value: 1, // unit
          savings,
          cumulativeSavings: Math.round(cumulativeSavings),
          ratio: savings,
        };
      });

    // Grouping for charts (simplified)
    const pauseMap: Record<string, number> = {};
    let totalPauses = 0;
    
    // Aggregating pause reasons across all historical data (assuming it's nested in tasks or metadata)
    // Se o backend enviar "pause_reasons_history" ou iterar as execuções
    historicalExecutions.forEach(e => {
      // Mock / placeholder logic if e doesn't have tasks populated:
      // Se houver e.metadata.pauseReasons, usamos
      const reasons = e.metadata?.pauseReasons || [];
      reasons.forEach((r: string) => {
        pauseMap[r] = (pauseMap[r] || 0) + 1;
        totalPauses++;
      });
    });

    let cumulative = 0;
    const pauseReasonPareto = Object.entries(pauseMap)
      .map(([reason, count]) => ({
        reason,
        count,
        percentage: totalPauses > 0 ? (count / totalPauses) * 100 : 0,
        cumulativePercentage: 0 // placeholder
      }))
      .sort((a, b) => b.count - a.count)
      .map(item => {
        cumulative += item.percentage;
        return { ...item, cumulativePercentage: cumulative };
      });

    if (totalPauses === 0) {
      // Default mock data to demonstrate the Pareto if empty
      pauseReasonPareto.push(
        { reason: 'Aguardando Cliente', count: 45, percentage: 45, cumulativePercentage: 45 },
        { reason: 'Falta de Documentação', count: 35, percentage: 35, cumulativePercentage: 80 },
        { reason: 'Fila de Análise', count: 12, percentage: 12, cumulativePercentage: 92 },
        { reason: 'Problema Técnico', count: 8, percentage: 8, cumulativePercentage: 100 }
      );
    }

    set({
      realtime: {
        currentWip: currentWip > 0 ? currentWip : activeCount,
        averageLeadTimeMinutes,
        activeCount,
        slaRiskPercentage,
        throughput
      },
      historical: {
        leadTimeTrend: [],
        throughputTrend: [],
        stagePerformance: [],
        financialImpact: impactSeries,
        pauseReasonPareto
      }
    });
  }
}));

export const useAnalyticsData = () => {
  const store = useAnalyticsStore();
  return {
    realtime: store.realtime,
    historical: store.historical,
    calculateMetrics: store.calculateMetrics,
  };
};

